// src/pages/Step5.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { scopedTransactionSelect, scopedTransactionUpdate } from '../../utils/secureQuery';
import { parseUuid } from '../../utils/inputValidation';
import Layout from "../../components/Layout";
import { useApp } from "../../context/AppContext"; // 🔥 AJUSTE: Importación del contexto
import { motion, AnimatePresence } from "framer-motion";
import { 
  Copy, Check, Info, Clock, 
  Wallet, ChevronLeft, AlertCircle, ArrowRight, CheckCircle2, ChevronDown, Landmark, Smartphone 
} from "lucide-react";

const METHOD_NAMES = {
  pagomovil: "Pago móvil",
  bank_transfer: "Transferencia bancaria",
  western: "Western Union",
  zelle: "Zelle",
  bancolombia: "Bancolombia",
};

const FIELD_LABELS = {
  holder_name: "Titular",
  document_type: "Tipo de documento",
  document_number: "Número de documento",
  account_number: "N° Cuenta / Teléfono",
  bank: "Banco",
  account_type: "Tipo de cuenta",
  instructions: "Instrucciones",
  platform: "Plataforma",
};

const COPIABLE_KEYS = [
  "phone", "email", "Llave", "account_number", "Numero de Telefono", "cedula"
];

const formatAmount = (amount, currency) => {
  if (amount == null) return "—";
  return `${amount.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`;
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const FieldRow = ({ label, value, isCopiable, isInstruction }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    const cleanValue = value.toString().replace(/[^\w\s@.-]/g, '').trim();
    navigator.clipboard.writeText(cleanValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!value && value !== 0) return null;

  return (
    <div className="flex justify-between items-start py-3 border-b border-dashed border-slate-200 last:border-b-0 gap-4">
      <span className="text-slate-500 text-[10px] md:text-[11px] font-black uppercase tracking-widest mt-1 shrink-0">{label}</span>
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <span className={`font-bold text-slate-800 text-right break-words ${isInstruction ? 'text-xs' : 'text-sm'}`}>
          {value}
        </span>
        {isCopiable && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handleCopy}
            className={`p-1.5 md:p-2 rounded-xl transition-all flex items-center justify-center shadow-sm border shrink-0
              ${copied ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200'}
            `}
            title="Copiar"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </motion.button>
        )}
      </div>
    </div>
  );
};

export default function Step5() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateTransactionStatus, user } = useApp();
  const { transactionId } = location.state || {};

  const [transaction, setTransaction] = useState(null);
  const [originMethod, setOriginMethod] = useState(null);
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [showDetails, setShowDetails] = useState(false); 
  const [showPushReference, setShowPushReference] = useState(false); 
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchTransaction = async () => {
      setLoading(true);
      if (!transactionId || !user?.id) {
        setLoading(false);
        return;
      }

      await scopedTransactionUpdate(supabase, transactionId, user.id, { current_step: "confirm" });

      const { data: trx, error: trxError } = await scopedTransactionSelect(
        supabase,
        transactionId,
        user.id
      ).single();

      if (trxError || !trx) {
        console.error("Error cargando transacción:", trxError);
        setLoading(false);
        return;
      }

      let userName = "Usuario desconocido";
      let userPhone = "No disponible";

      if (trx.user_id) {
        const { data: usr, error: usrError } = await supabase.from("users").select("first_name, last_name, phone").eq("id", trx.user_id).single();
        if (!usrError && usr) {
          userName = `${usr.first_name || ""} ${usr.last_name || ""}`.trim() || "Usuario";
          userPhone = usr.phone || "No disponible";
        }
      }

      setTransaction({ ...trx, user_name: userName, user_phone: userPhone });

      if (trx.payment_method_origin_id) {
        const { data: origin, error: originError } = await supabase
          .from("payment_methods_origin")
          .select("*")
          .eq("id", parseUuid(trx.payment_method_origin_id, 'Método origen'))
          .single();
        if (!originError) setOriginMethod(origin);
      }

      if (trx.timer_started_at && trx.timer_duration != null) {
        const startedAtUTC = trx.timer_started_at.endsWith('Z') ? trx.timer_started_at : trx.timer_started_at + 'Z';
        const startTime = new Date(startedAtUTC).getTime();
        const elapsedSeconds = Math.max(Math.floor((Date.now() - startTime) / 1000), 0);
        setTimeLeft(Math.max(trx.timer_duration - elapsedSeconds, 0));
      } else {
        setTimeLeft(600);
      }

      setLoading(false);
    };

    fetchTransaction();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [transactionId, user?.id]);

  useEffect(() => {
    if (showBanner || timeLeft <= 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timeLeft, showBanner]);

  const handlePaymentDone = async () => {
    if (!transaction) return;
    
    if (!reference.trim()) {
      alert("Debes ingresar la referencia de pago");
      return;
    }

    setSubmitting(true);

    try {
      // 🔥 AJUSTE CLAVE: Usamos la función del contexto para actualizar el estado
      // Esto disparará automáticamente la notificación a OneSignal a través de la Edge Function
      await updateTransactionStatus(transaction.id, "Procesando", reference.trim());

      setShowBanner(true);
      setTimeout(() => navigate("/history"), 2000);
    } catch (err) {
      console.error("Error actualizando transacción:", err);
      alert("Error al registrar el pago");
      setSubmitting(false);
    }
  };

  const handleMainAction = () => {
    const isPushMethod = originMethod?.is_push;
    if (isPushMethod && !showPushReference) {
      setShowPushReference(true);
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
    } else {
      handlePaymentDone();
    }
  };

  const handleBack = () => navigate(-1);

  if (loading) {
    return (
      <Layout showNavigation={false}>
        <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-blue-900/70 font-black uppercase tracking-widest text-[10px]">Generando instrucciones...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const renderMethodFields = (data) => {
    if (!data) return null;
    const methodName = METHOD_NAMES[data.method] || data.method;

    return (
      <div className="space-y-1">
        <FieldRow label="Método" value={methodName} isCopiable={false} />
        {data.instructions && <FieldRow label="Instrucciones" value={data.instructions} isCopiable={false} isInstruction={true} />}
        {data.data && Object.entries(data.data).filter(([_, value]) => value).map(([key, value]) => {
          if (key === "bank" && value === methodName) return null;
          const label = FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
          const canCopy = COPIABLE_KEYS.includes(key);
          return <FieldRow key={key} label={label} value={value} isCopiable={canCopy} />;
        })}
      </div>
    );
  };

  const isPushMethod = originMethod?.is_push;

  const isButtonDisabled = 
    submitting || 
    showBanner || 
    timeLeft === 0 || 
    (isPushMethod && showPushReference && !reference.trim()) ||
    (!isPushMethod && !reference.trim());

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen bg-[#f4f7fb] px-4 py-4 md:py-8 relative">
        
        <AnimatePresence>
          {showBanner && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-blue-600/95 backdrop-blur-md px-4"
            >
              <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} className="text-center text-white">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <CheckCircle2 className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-2">
                  ¡Aprobación Registrada!
                </h2>
                <p className="font-medium text-blue-100 text-sm md:text-base">Validaremos tu transacción en breve.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mx-auto w-full max-w-md">
          
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-5 md:mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-full mb-3 shadow-sm">
              {isPushMethod ? <Smartphone className="w-5 h-5" /> : <Wallet className="w-5 h-5" />}
            </div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight leading-tight">
              {isPushMethod ? "Autoriza el cobro" : "Realiza el pago"}
            </h1>
            <p className="text-slate-500 text-xs font-medium mt-1">
              {isPushMethod ? "Revisa las notificaciones de tu celular." : "Transfiere a la cuenta indicada abajo."}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-4">
            
            {transaction && (
              <div className="px-4 py-3 bg-amber-50 border border-amber-200/60 rounded-xl flex items-start gap-3 shadow-sm">
                <AlertCircle className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
                <p className="text-[9px] md:text-[11px] font-medium text-amber-800 leading-tight">
                  Realiza el pago desde tu cuenta personal a nombre de <span className="font-black">{transaction.user_name || "Usuario"}</span> registrado con el número <span className="font-black">{transaction.user_phone || "—"}</span>.
                </p>
              </div>
            )}

            {transaction && (
              <div className="bg-white rounded-[24px] shadow-sm border border-slate-100 overflow-hidden">
                <button 
                  onClick={() => setShowDetails(!showDetails)}
                  className="w-full p-5 md:p-6 text-center hover:bg-slate-50 transition-colors flex flex-col items-center relative"
                >
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1.5">
                    Monto exacto a pagar <Info className="w-3 h-3" />
                  </span>
                  
                  <span className="text-3xl md:text-[34px] font-black text-blue-600 tracking-tighter leading-none mb-2">
                    {formatAmount(transaction.total_to_pay, transaction.origin_currency)}
                  </span>
                  
                  <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
                    <span>{showDetails ? 'Ocultar detalle' : 'Ver detalle'}</span>
                    <motion.div animate={{ rotate: showDetails ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </motion.div>
                  </div>
                </button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-slate-50/50"
                    >
                      <div className="p-4 md:p-5 border-t border-dashed border-slate-200 space-y-2.5">
                        <div className="flex justify-between items-center bg-white px-3 py-2 rounded-lg border border-slate-100 shadow-sm">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tasa aplicada</span>
                          <span className="font-bold text-slate-700 text-xs">{transaction.rate}</span>
                        </div>
                        <div className="flex justify-between items-center px-1">
                          <span className="text-slate-500 font-medium text-xs">Monto del envío</span>
                          <span className="font-bold text-slate-800 text-xs">
                            {formatAmount(transaction.amount, transaction.origin_currency)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center px-1 pb-1">
                          <span className="text-slate-500 font-medium text-xs">Comisión de plataforma</span>
                          {Number(transaction.payment_fee) === 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-100/80 text-emerald-700 text-[9px] font-black uppercase tracking-widest rounded border border-emerald-200/50">
                              Gratis
                            </span>
                          ) : (
                            <span className="font-bold text-slate-500 text-xs">
                              + {formatAmount(transaction.payment_fee, transaction.origin_currency)}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {isPushMethod ? (
              <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-slate-100 text-center">
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-indigo-100">
                  <Smartphone className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-black text-slate-800 mb-2 tracking-tight">Revisa tu aplicación</h3>
                <p className="text-[13px] font-medium text-slate-500 leading-relaxed mb-4">
                  Hemos enviado una solicitud de cobro a tu número. Entra a tu app, aprueba el pago por el monto exacto y luego presiona el botón de abajo.
                </p>

                <AnimatePresence>
                  {showPushReference && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden text-left"
                    >
                      <div className="pt-5 border-t border-dashed border-slate-200">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                          N° de Referencia / Comprobante
                        </label>
                        <input
                          type="text"
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          placeholder="Ej: 123456789"
                          className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 transition-all font-bold text-slate-800 placeholder-slate-400 outline-none text-[15px] shadow-inner"
                          disabled={submitting || showBanner || timeLeft === 0}
                        />
                        <p className="mt-2 text-[10px] font-bold text-slate-400 leading-tight">
                          Ingresa el número de comprobante que te dio la app al aprobar.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <a 
                  href="https://wa.me/573241437215?text=Hola,%20no%20he%20recibido%20la%20notificaci%C3%B3n%20de%20pago%20Push%20en%20mi%20app." 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-block mt-5 mb-6 text-[11px] font-black tracking-wide text-indigo-500 hover:text-indigo-700 underline underline-offset-4 transition-colors"
                >
                  No recibí la notificación de pago
                </a>

                <div>
                  {timeLeft > 0 ? (
                    <div className="inline-flex items-center justify-center w-full gap-2 px-4 py-3 bg-indigo-50 text-indigo-700 rounded-xl font-bold text-xs border border-indigo-100 shadow-sm">
                      <Clock className="w-4 h-4" /> Esperando aprobación: {formatTime(timeLeft)}
                    </div>
                  ) : (
                    <div className="inline-flex items-center justify-center w-full gap-2 px-4 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-xs border border-slate-200">
                      <AlertCircle className="w-4 h-4" /> El tiempo ha expirado.
                    </div>
                  )}
                </div>
              </div>

            ) : (
              <>
                {originMethod && (
                  <div className="bg-white rounded-[24px] p-5 md:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600"><Landmark className="w-4 h-4" /></div>
                      <h3 className="font-black text-slate-800 text-[13px] uppercase tracking-widest">Datos Bancarios</h3>
                    </div>
                    <div className="bg-slate-50 px-4 py-1 rounded-2xl border border-slate-100/80">
                      {renderMethodFields(originMethod)}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-[24px] p-5 md:p-6 shadow-sm border border-slate-100">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                    N° de Referencia / Comprobante
                  </label>
                  <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Ej: 123456789"
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 transition-all font-bold text-slate-800 placeholder-slate-400 outline-none text-[15px] shadow-inner"
                    disabled={submitting || showBanner || timeLeft === 0}
                  />
                  <p className="mt-2 text-[10px] font-bold text-slate-400 leading-tight">
                    Ingresa el número que te proporcionó el banco al terminar la transferencia.
                  </p>
                  
                  <div className="mt-5">
                    {timeLeft > 0 ? (
                      <div className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl font-bold text-xs border border-red-100 shadow-sm">
                        <Clock className="w-4 h-4" /> Paga y reporta en: {formatTime(timeLeft)}
                      </div>
                    ) : (
                      <div className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-slate-100 text-slate-500 rounded-xl font-bold text-xs border border-slate-200">
                        <AlertCircle className="w-4 h-4" /> El tiempo ha expirado.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </motion.div>

          <div className="h-28 w-full"></div>
        </div>

        <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-xl border-t border-slate-200/50 p-3.5 sm:p-5 z-40 pb-safe shadow-[0_-5px_20px_rgba(0,0,0,0.03)]">
          <div className="max-w-md mx-auto flex gap-3">
            <button
              onClick={handleBack}
              disabled={submitting || showBanner}
              className="flex items-center justify-center p-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl transition-all active:scale-95 disabled:opacity-50 border border-slate-200/50"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleMainAction}
              disabled={isButtonDisabled}
              className={`
                flex-1 py-3.5 px-6 rounded-2xl font-black text-[14px] transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-widest
                ${isButtonDisabled
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none"
                  : isPushMethod 
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_8px_20px_rgba(79,70,229,0.25)]" 
                    : "bg-blue-600 hover:bg-blue-700 text-white shadow-[0_8px_20px_rgb(37,99,235,0.25)]"
                }
              `}
            >
              {submitting ? (
                <div className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  {isPushMethod && !showPushReference ? "Ya aprobé el cobro" : "Notificar Pago"} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}

